import { useCallback, useEffect, useMemo, useState } from 'react';

import { AiChatDialogPanel } from '../components/AiChatDialogPanel';
import type { QaSegmentSelectionController } from '../components/KnowledgeParticleQaSections';
import { TtqSegmentSelectionPopup } from '../features/ttqib/TtqSegmentSelectionPopup';
import { listAiChatAnchors } from '../services/userAiChatService';
import type { KnowledgeParticle } from '../types/domain';
import { buildAiChatSourceParticles } from '../utils/aiChatSourceParticles';
import type { AiChatSegmentAnchor } from '../utils/aiChatSegmentKey';

type TrayItem = {
  objectId: string;
  particleId: string;
  text: string;
};

type UseKnowledgeParticleSegmentChatOptions = {
  entrypoint?: string;
  surface?: string;
  subject?: string;
  onParticleUpdated?: (particle: KnowledgeParticle) => void;
};

/**
 * 屏幕级选段 → Popup → AI 对话（Web：Popup 为 fixed sibling，无 RN Modal）。
 */
export function useKnowledgeParticleSegmentChat(
  options: UseKnowledgeParticleSegmentChatOptions = {},
) {
  const {
    entrypoint = 'kp-detail',
    surface = 'kp_detail',
    subject,
    onParticleUpdated,
  } = options;

  const [tray, setTray] = useState<TrayItem[]>([]);
  const [anchorObjectId, setAnchorObjectId] = useState<string | null>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatThreadId, setChatThreadId] = useState<string | null>(null);
  const [chatContext, setChatContext] = useState<TrayItem[]>([]);
  const [chatEntrypoint, setChatEntrypoint] = useState(entrypoint);
  const [activeParticle, setActiveParticle] = useState<KnowledgeParticle | null>(null);
  const [chatAnchorsByParticle, setChatAnchorsByParticle] = useState<Record<string, AiChatSegmentAnchor[]>>({});

  const refreshAnchors = useCallback(async (particleId: string) => {
    if (!particleId) return;
    try {
      const anchors = await listAiChatAnchors(particleId);
      setChatAnchorsByParticle((prev) => ({ ...prev, [particleId]: anchors }));
    } catch {
      setChatAnchorsByParticle((prev) => ({ ...prev, [particleId]: [] }));
    }
  }, []);

  useEffect(() => {
    if (!activeParticle?.id) return;
    void refreshAnchors(activeParticle.id);
  }, [activeParticle?.id, refreshAnchors]);

  const toggleSegment = useCallback((
    item: { objectId: string; text: string; particleId: string },
    particle: KnowledgeParticle,
  ) => {
    setActiveParticle(particle);
    setTray((prev) => {
      const sameParticle = prev.length === 0 || prev.every((entry) => entry.particleId === item.particleId);
      const base = sameParticle ? prev : [];
      const exists = base.some((entry) => entry.objectId === item.objectId);
      const next = exists
        ? base.filter((entry) => entry.objectId !== item.objectId)
        : [...base, { objectId: item.objectId, particleId: item.particleId, text: item.text }];
      if (next.length === 0) {
        setPopupOpen(false);
        setAnchorObjectId(null);
      } else {
        setPopupOpen(true);
        setAnchorObjectId(item.objectId);
      }
      return next;
    });
  }, []);

  const openChatThread = useCallback((threadId: string, particle: KnowledgeParticle) => {
    setActiveParticle(particle);
    setChatContext([]);
    setChatThreadId(threadId);
    setChatEntrypoint(entrypoint);
    setPopupOpen(false);
    setChatOpen(true);
  }, [entrypoint]);

  const openChatFromTray = useCallback((nextEntrypoint: string = entrypoint) => {
    setChatContext(tray);
    setChatThreadId(null);
    setChatEntrypoint(nextEntrypoint);
    setPopupOpen(false);
    setChatOpen(true);
    setTray([]);
    setAnchorObjectId(null);
  }, [entrypoint, tray]);

  const getSegmentSelection = useCallback((particle: KnowledgeParticle | null | undefined): QaSegmentSelectionController | undefined => {
    if (!particle?.id) return undefined;
    const anchors = chatAnchorsByParticle[particle.id] || [];
    const particleChatThreadId = anchors.find((item) => String(item.threadId || '').trim())?.threadId || null;
    return {
      selectedObjectIds: tray
        .filter((item) => item.particleId === particle.id)
        .map((item) => item.objectId),
      // KP：不按段挂对话图标，只保留颗粒级入口
      chatAnchors: [],
      particleChatThreadId,
      onToggleSegment: (item) => toggleSegment(item, particle),
      onOpenChatThread: (threadId) => openChatThread(threadId, particle),
    };
  }, [chatAnchorsByParticle, openChatThread, toggleSegment, tray]);

  const sourceParticles = useMemo(
    () => buildAiChatSourceParticles(
      activeParticle ? [{
        particleId: activeParticle.id,
        particleTitle: activeParticle.title,
        particleQuestions: activeParticle.questions as string | null | undefined,
        particleAnswers: activeParticle.answers as string | null | undefined,
      }] : [],
      activeParticle?.id ? [activeParticle.id] : [],
    ),
    [activeParticle],
  );

  const clearSelection = useCallback(() => {
    setTray([]);
    setPopupOpen(false);
    setAnchorObjectId(null);
  }, []);

  const showPopup = popupOpen && !chatOpen && tray.length > 0;

  const overlays = (
    <>
      <TtqSegmentSelectionPopup
        open={showPopup}
        items={tray}
        anchorObjectId={anchorObjectId}
        chatOnly
        onOpenChat={() => openChatFromTray(entrypoint)}
        onOpenPinTi={() => setPopupOpen(false)}
        onReportError={() => openChatFromTray('kp-error-feedback')}
        onClose={() => setPopupOpen(false)}
        onClear={clearSelection}
      />

      <AiChatDialogPanel
        open={chatOpen}
        onClose={() => {
          setChatOpen(false);
          setChatThreadId(null);
          setChatContext([]);
          setChatEntrypoint(entrypoint);
          if (activeParticle?.id) void refreshAnchors(activeParticle.id);
        }}
        particleId={activeParticle?.id}
        particleTitle={activeParticle?.title || undefined}
        contextBlocks={chatContext.map((item) => ({
          id: item.objectId,
          kind: 'text_segment' as const,
          particleId: item.particleId,
          text: item.text,
        }))}
        sourceParticles={sourceParticles}
        entrypoint={chatEntrypoint}
        surface={surface}
        subject={subject || activeParticle?.subject || undefined}
        threadId={chatThreadId}
        onParticleUpdated={onParticleUpdated}
      />
    </>
  );

  return {
    getSegmentSelection,
    overlays,
    clearSelection,
  };
}
